import { bandLabel } from '@quoting/ladder'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { HistoryPanel, type HistoryResponse } from '../../components/History'
import { Ladder } from '../../components/Ladder'
import { PriceHeader } from '../../components/PriceHeader'
import { DataTable, ErrorBox, Field, Panel, ScreenHeading, Signed, Spinner, Tag, type TagTone } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useT } from '../../lib/i18n'
import { amount, dateTime, money, pct, ppiLabel, signedAmount } from '../../lib/format'
import type { QuoteView } from '../../lib/types'

export const STATUS_TONE: Record<string, TagTone> = { recorded: 'pos', pending: 'warn', approved: 'pos', rejected: 'neg', withdrawn: 'neutral' }
export const BAND_TONE: Record<string, TagTone> = { within: 'pos', escalate: 'warn', below_floor: 'neg' }
export const OUTCOME_TONE: Record<string, TagTone> = { open: 'neutral', won: 'pos', lost: 'neg' }

export function Quotes() {
  const { id } = useParams()
  if (id) return <QuoteDetail id={id} />
  return <QuoteList />
}

function QuoteList() {
  const { user } = useAuth()
  const { t } = useT()
  const navigate = useNavigate()
  const [rows, setRows] = useState<QuoteView[] | null>(null)
  const [perf, setPerf] = useState<{ total: { ppi: number | null; quotedPpi: number | null; won: number }; months: { won: number; lost: number }[] } | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    api.get<QuoteView[]>('/api/quotes').then(setRows).catch((e) => setError(errorMessage(e)))
    if (user?.role === 'rep') api.get<typeof perf>('/api/team?months=6').then(setPerf).catch(() => {})
  }, [user?.role])
  const tone = (v: number | null) => (v === null ? undefined : v < 100 ? ('neg' as const) : ('pos' as const))
  const lost = perf ? perf.months.reduce((s, m) => s + m.lost, 0) : 0
  return (
    <div>
      <ScreenHeading
        title={user?.role === 'supervisor' ? t('Team quotes') : t('My quotes')}
        stats={
          perf
            ? [
                { label: t('Your PPI'), value: ppiLabel(perf.total.ppi), tone: tone(perf.total.ppi), foot: t('final price ÷ target × 100, won quotes') },
                { label: t('Quoted PPI'), value: ppiLabel(perf.total.quotedPpi), tone: tone(perf.total.quotedPpi), foot: t('quoted or approved price') },
                { label: t('Won / lost'), value: `${perf.total.won} / ${lost}`, foot: perf.total.won + lost ? `${pct(perf.total.won / (perf.total.won + lost), 0)} ${t('win rate on decided')}` : t('no outcomes yet') },
              ]
            : undefined
        }
      />
      {error && <ErrorBox>{error}</ErrorBox>}
      {!rows ? (
        <Spinner />
      ) : (
        <Panel>
          <DataTable
            rows={rows}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/quotes/${r.id}`)}
            empty={t('No quotes yet.')}
            columns={[
              { key: 'at', header: t('When'), render: (r) => <span className="text-slate-400">{dateTime(r.createdAt)}</span> },
              ...(user?.role === 'supervisor' ? [{ key: 'who', header: t('Rep'), render: (r: QuoteView) => r.createdByName }] : []),
              { key: 'p', header: t('Article'), render: (r) => <span className="text-slate-200">{r.productName}</span> },
              { key: 'c', header: t('Customer'), render: (r) => r.customerName },
              { key: 'target', header: t('Target'), align: 'right', render: (r) => amount(r.target) },
              { key: 'ask', header: t('Ask'), align: 'right', render: (r) => <span className="text-slate-500">{amount(r.ask)}</span> },
              { key: 'floor', header: t('Floor'), align: 'right', render: (r) => <span className="text-slate-500">{amount(r.floor)}</span> },
              { key: 'intended', header: t('Quoted'), align: 'right', render: (r) => <span className="font-semibold text-slate-100">{amount(r.escalation?.decidedPrice ?? r.intendedPrice)}</span> },
              {
                key: 'ppi',
                header: t('PPI'),
                align: 'right',
                render: (r) => {
                  // the final price once won, else the quoted (or approved) price
                  const price = r.outcome === 'won' && r.finalPrice !== null ? r.finalPrice : (r.escalation?.decidedPrice ?? r.intendedPrice)
                  const v = r.target > 0 ? (price / r.target) * 100 : null
                  return v === null ? '–' : <Signed value={v - 100}>{ppiLabel(v)}</Signed>
                },
              },
              { key: 'band', header: t('Band'), render: (r) => <Tag tone={BAND_TONE[r.band]}>{t(bandLabel(r.band))}</Tag> },
              { key: 'status', header: t('Status'), render: (r) => <Tag tone={STATUS_TONE[r.status] ?? 'neutral'}>{t(r.status)}</Tag> },
              { key: 'outcome', header: t('Outcome'), render: (r) => (
                <span className="flex items-center gap-1.5">
                  <Tag tone={OUTCOME_TONE[r.outcome] ?? 'neutral'}>{t(r.outcome)}</Tag>
                  {r.outcome === 'won' && r.finalPrice !== null && <span className="tabular text-slate-300">{amount(r.finalPrice)}</span>}
                </span>
              ) },
            ]}
          />
        </Panel>
      )}
    </div>
  )
}

/** The quote exactly as it was priced: everything below comes from the stored snapshot, nothing is recomputed. */
export function QuoteDetail({ id, embedded = false }: { id: string; embedded?: boolean }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { t } = useT()
  const [q, setQ] = useState<QuoteView | null>(null)
  const [history, setHistory] = useState<HistoryResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<'open' | 'won' | 'lost'>('open')
  const [finalPrice, setFinalPrice] = useState('')
  const [outcomeNote, setOutcomeNote] = useState('')
  const [savingOutcome, setSavingOutcome] = useState(false)
  const canSetOutcome = !!user && user.role !== 'admin' && (user.role !== 'rep' || q?.createdBy === user.id)
  const saveOutcome = async () => {
    if (!q) return
    setSavingOutcome(true)
    setError(null)
    try {
      const v = await api.post<QuoteView>(`/api/quotes/${q.id}/outcome`, {
        outcome,
        finalPrice: outcome === 'won' ? Number(finalPrice.replace(',', '.')) : undefined,
        note: outcomeNote || undefined,
      })
      setQ(v)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSavingOutcome(false)
    }
  }
  useEffect(() => {
    api
      .get<QuoteView>(`/api/quotes/${id}`)
      .then((v) => {
        setQ(v)
        setOutcome(v.outcome)
        setFinalPrice(v.finalPrice !== null ? String(v.finalPrice) : '')
        setOutcomeNote(v.outcomeNote ?? '')
        return api.get<HistoryResponse>(`/api/customers/${encodeURIComponent(v.customerId)}/history?productId=${encodeURIComponent(v.productId)}&month=${v.snapshot.context.month}`).then(setHistory)
      })
      .catch((e) => setError(errorMessage(e)))
  }, [id])
  if (error && !q) return <ErrorBox>{error}</ErrorBox>
  if (!q) return <Spinner />
  const r = q.snapshot.result
  const quoted = q.escalation?.decidedPrice ?? q.intendedPrice
  return (
    <div className="space-y-4">
      {!embedded && (
        <div className="flex items-center justify-between gap-4 pb-1">
          <div className="text-[13px] text-slate-500">
            {t('Quoted {when} by {who}', { when: dateTime(q.createdAt), who: q.createdByName })} · {t('model')} {q.modelVersionId.slice(0, 8)} · {q.snapshot.context.month}
          </div>
          <button type="button" className="btn btn-sm" onClick={() => navigate(-1)}>
            {t('Back')}
          </button>
        </div>
      )}
      <PriceHeader
        p={r}
        title={
          <>
            {q.productName} <span className="font-normal text-slate-500">×</span> {q.customerName}
          </>
        }
        freight={q.snapshot.context.freight ?? null}
        intended={{ value: quoted, label: q.outcome === 'won' && q.finalPrice !== null ? t('Final price') : q.escalation?.decidedPrice != null ? t('Approved') : t('Quoted'), tone: q.band === 'within' ? 'pos' : q.band === 'escalate' ? 'warn' : 'neg' }}
        right={
          <div>
            <div className="label">{t('Quoted')}</div>
            <div className="text-2xl font-semibold tabular text-slate-50">{money(quoted, r.currency)}</div>
            <div className="mt-1 flex items-center gap-2">
              <Tag tone={BAND_TONE[q.band]}>{t(bandLabel(q.band))}</Tag>
              <Tag tone={STATUS_TONE[q.status] ?? 'neutral'}>{t(q.status)}</Tag>
              {q.outcome !== 'open' && <Tag tone={OUTCOME_TONE[q.outcome]}>{t(q.outcome)}{q.outcome === 'won' && q.finalPrice !== null ? ` ${t('at')} ${amount(q.finalPrice)}` : ''}</Tag>}
            </div>
          </div>
        }
      />
      {!embedded && canSetOutcome && (
        <Panel title={t('Outcome')}>
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('Outcome')}>
              <select className="field w-40" value={outcome} onChange={(e) => setOutcome(e.target.value as 'open' | 'won' | 'lost')}>
                <option value="open">{t('Undecided')}</option>
                <option value="won">{t('Won')}</option>
                <option value="lost">{t('Lost')}</option>
              </select>
            </Field>
            {outcome === 'won' && (
              <Field label={`${t('Final price')}, ${r.currency} ${t('per unit')}`}>
                <input className="field w-40 tabular" inputMode="decimal" value={finalPrice} onChange={(e) => setFinalPrice(e.target.value)} placeholder={amount(quoted)} />
              </Field>
            )}
            <Field label={t('Note (optional)')}>
              <input className="field w-[24rem] max-w-full" value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} placeholder={t('PO received, lost to competitor…')} />
            </Field>
            <button
              type="button"
              className="btn btn-primary"
              disabled={savingOutcome || (outcome === 'won' && !(Number(finalPrice.replace(',', '.')) > 0)) || (outcome === q.outcome && (q.finalPrice ?? '') === (outcome === 'won' ? Number(finalPrice.replace(',', '.')) : '') && (q.outcomeNote ?? '') === outcomeNote)}
              onClick={saveOutcome}
            >
              {savingOutcome ? t('Saving…') : t('Save outcome')}
            </button>
            {q.outcome !== 'open' && q.outcomeAt && <span className="text-xs text-slate-500">{t('recorded')} {dateTime(q.outcomeAt)}</span>}
            {outcome === 'won' && Number(finalPrice.replace(',', '.')) > 0 && (
              <span className="text-xs text-slate-400">
                {t('vs target')} <Signed value={Number(finalPrice.replace(',', '.')) - r.target}>{signedAmount(Number(finalPrice.replace(',', '.')) - r.target)}</Signed>
              </span>
            )}
          </div>
          {error && <div className="mt-3"><ErrorBox>{error}</ErrorBox></div>}
        </Panel>
      )}
      {q.escalation && (
        <Panel title={t('Escalation')} subtitle={`${q.escalation.level === 'second' ? t('To the general manager') : t('To the sales director')} · ${t('requested')} ${dateTime(q.escalation.requestedAt)}`}>
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <div className="label">{t('Asked for')}</div>
              <div className="text-lg font-semibold tabular text-slate-100">{money(q.escalation.requestedPrice, r.currency)}</div>
              <div className="text-xs text-slate-400">
                <Signed value={q.escalation.requestedPrice - r.target}>{signedAmount(q.escalation.requestedPrice - r.target)}</Signed> {t('vs target')}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="label">{t('Reason')}</div>
              <p className="text-sm text-slate-300">{q.escalation.reason}</p>
            </div>
            {q.escalation.status !== 'pending' && (
              <div className="md:col-span-3 rounded-md border border-slate-800 bg-slate-900/40 p-3">
                <div className="flex items-center gap-2">
                  <Tag tone={STATUS_TONE[q.escalation.status] ?? 'neutral'}>{t(q.escalation.status)}</Tag>
                  {q.escalation.decidedPrice !== null && <span className="text-sm tabular text-slate-100">{t('at')} {money(q.escalation.decidedPrice, r.currency)}</span>}
                  <span className="text-xs text-slate-500">
                    {t('by')} {q.escalation.decidedByName ?? '–'} · {dateTime(q.escalation.decidedAt)}
                  </span>
                </div>
                {q.escalation.decisionNote && <p className="mt-1.5 text-sm text-slate-300">{q.escalation.decisionNote}</p>}
              </div>
            )}
          </div>
        </Panel>
      )}
      <Ladder ladder={r.ladder} target={r.target} currency={r.currency} />
      <HistoryPanel data={history} target={r.target} loading={!history} productId={q.productId} />
    </div>
  )
}
